import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Banner } from '../../../types/Banner';

interface Props {
  banners: Banner[];
  height?: number;
}

export default function SidebarBannerCarousel({ banners, height = 140 }: Props) {
  const { i18n } = useTranslation();
  const [idx, setIdx] = useState(0);

  const items = useMemo(() => {
    const now = Date.now();
    return (banners || [])
      .filter(b => b?.is_active)
      .filter(b => {
        const from = b.publish_from ? new Date(b.publish_from).getTime() : null;
        const to = b.publish_to ? new Date(b.publish_to).getTime() : null;
        if (from != null && now < from) return false;
        if (to != null && now > to) return false;
        return true;
      })
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  }, [banners]);

  useEffect(() => {
    if (items.length <= 1) return;
    const id = window.setInterval(() => {
      setIdx(s => (s + 1) % items.length);
    }, 6500);
    return () => window.clearInterval(id);
  }, [items.length]);

  useEffect(() => {
    if (idx >= items.length) setIdx(0);
  }, [idx, items.length]);

  const current = items[idx] || null;
  const isEs = i18n.language.startsWith('es');
  const imageUrl = current ? (isEs ? current.image_es_url : current.image_en_url) : null;

  if (!current || !imageUrl) return null;

  const content = (
    <div
      style={{
        position: 'relative',
        borderRadius: 14,
        overflow: 'hidden',
        border: '1px solid #eee',
        background: '#fff'
      }}
    >
      <img
        src={imageUrl}
        alt={current.title || 'Banner'}
        style={{ width: '100%', height, objectFit: 'cover', display: 'block' }}
        onError={e => (e.currentTarget.style.display = 'none')}
      />

      {current.title ? (
        <div
          style={{
            position: 'absolute',
            left: 10,
            right: 10,
            bottom: 10,
            padding: '8px 10px',
            borderRadius: 12,
            background: 'rgba(0,0,0,0.55)',
            color: '#fff',
            fontWeight: 800,
            fontSize: 12,
            lineHeight: 1.2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10
          }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{current.title}</span>
          {items.length > 1 ? (
            <span style={{ fontSize: 11, opacity: 0.9, fontWeight: 900 }}>
              {idx + 1}/{items.length}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );

  if (current.link_url) {
    return (
      <a href={current.link_url} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
        {content}
      </a>
    );
  }

  return content;
}
